import React, { useState } from 'react';
import './capabilities.css';

export default function Capabilities() {
  const [activeTab, setActiveTab] = useState('agents');

  const tabs = [
    { id: 'agents', label: 'Agentic Workflows' },
    { id: 'rag', label: 'Retrieval Pipelines (RAG)' },
    { id: 'mlops', label: 'Production MLOps & Infra' }
  ];

  return (
    <div className="capabilities-container">
      <div className="capabilities-header">
        <span className="section-tag">Capabilities</span>
        <h2 className="section-title">Architecting Production-Ready AI Systems</h2>
        <p className="section-subtitle">
          I build scalable backends and intelligent agents that move beyond simple prompt engineering into robust production pipelines.
        </p>
      </div>

      <div className="capabilities-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="capability-display premium-card">
        {activeTab === 'agents' && (
          <div className="capability-content fade-in">
            <div className="capability-info">
              <h3>Multi-Agent Orchestration</h3>
              <p>
                Building stateful multi-agent workflows using LangGraph and LangChain. I design self-correcting routing loops where specialized agent nodes process tasks, write tests, evaluate outputs, and recursively correct errors.
              </p>
              <ul className="cap-list">
                <li><strong>State Management:</strong> Durable memory states across agent boundaries.</li>
                <li><strong>Validation Gates:</strong> Guardrails checking outputs against strict formatting schemas.</li>
                <li><strong>Tool Calling:</strong> Parallel API execution, context retrieval, and function calling.</li>
              </ul>
            </div>
            <div className="capability-visual">
              <svg viewBox="0 0 400 300" width="100%" height="100%" className="visual-svg">
                <defs>
                  <marker id="arrow" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                    <path d="M 0 0 L 10 5 L 0 10 z" fill="#555" />
                  </marker>
                </defs>
                {/* Nodes */}
                <rect x="20" y="120" width="80" height="40" rx="6" fill="#111" stroke="rgba(255,255,255,0.1)" />
                <text x="60" y="144" fill="#EDEDED" fontSize="10" textAnchor="middle" fontFamily="monospace">User Query</text>

                <path d="M 100 140 L 140 140" stroke="#555" strokeWidth="1" markerEnd="url(#arrow)" />

                <rect x="150" y="100" width="100" height="80" rx="8" fill="#1C1C1E" stroke="#4F46E5" strokeWidth="1.5" />
                <text x="200" y="130" fill="#EDEDED" fontSize="11" fontWeight="bold" textAnchor="middle">Supervisor</text>
                <text x="200" y="150" fill="#8F8F93" fontSize="9" textAnchor="middle" fontFamily="monospace">Routing State</text>

                {/* Agent 1 */}
                <path d="M 200 100 L 200 50 L 260 50" stroke="#555" strokeWidth="1" strokeDasharray="3" markerEnd="url(#arrow)" />
                <rect x="270" y="30" width="100" height="40" rx="6" fill="#111" stroke="rgba(255,255,255,0.1)" />
                <text x="320" y="54" fill="#EDEDED" fontSize="10" textAnchor="middle" fontFamily="monospace">Search Agent</text>

                {/* Agent 2 */}
                <path d="M 250 140 L 290 140" stroke="#555" strokeWidth="1" strokeDasharray="3" markerEnd="url(#arrow)" />
                <rect x="300" y="120" width="80" height="40" rx="6" fill="#111" stroke="rgba(255,255,255,0.1)" />
                <text x="340" y="144" fill="#EDEDED" fontSize="10" textAnchor="middle" fontFamily="monospace">Code Agent</text>

                {/* Validation Node */}
                <path d="M 200 180 L 200 230 L 140 230" stroke="#555" strokeWidth="1" markerEnd="url(#arrow)" />
                <rect x="30" y="210" width="100" height="40" rx="6" fill="#111" stroke="#10b981" strokeWidth="1" />
                <text x="80" y="234" fill="#10b981" fontSize="10" textAnchor="middle" fontFamily="monospace">Validator</text>

                {/* Return arrow */}
                <path d="M 80 210 L 80 180" stroke="#10b981" strokeWidth="1" strokeDasharray="3" markerEnd="url(#arrow)" />
              </svg>
            </div>
          </div>
        )}

        {activeTab === 'rag' && (
          <div className="capability-content fade-in">
            <div className="capability-info">
              <h3>Advanced Retrieval (RAG)</h3>
              <p>
                Going beyond simple keyword searches. I build high-performance retrieval pipelines featuring query expansion, document chunk optimization, vector search via FAISS and PostgreSQL (pgvector), cross-encoder reranking, and metadata filtering.
              </p>
              <ul className="cap-list">
                <li><strong>Hybrid Search:</strong> Combining BM25 keyword matching with dense vector embeddings.</li>
                <li><strong>Semantic Reranking:</strong> Applying cross-encoder models to filter retrieved contexts.</li>
                <li><strong>Evaluation Frameworks:</strong> Utilizing Ragas and TruLens to validate retrieve recall and faithfulness.</li>
              </ul>
            </div>
            <div className="capability-visual">
              <svg viewBox="0 0 400 300" width="100%" height="100%" className="visual-svg">
                <defs>
                  <marker id="arrow" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                    <path d="M 0 0 L 10 5 L 0 10 z" fill="#555" />
                  </marker>
                </defs>
                {/* Pipeline nodes */}
                <rect x="20" y="40" width="100" height="35" rx="6" fill="#111" stroke="rgba(255,255,255,0.1)" />
                <text x="70" y="61" fill="#EDEDED" fontSize="9" textAnchor="middle" fontFamily="monospace">Documents</text>

                <path d="M 70 75 L 70 105" stroke="#555" strokeWidth="1" markerEnd="url(#arrow)" />

                <rect x="20" y="110" width="100" height="35" rx="6" fill="#111" stroke="rgba(255,255,255,0.1)" />
                <text x="70" y="131" fill="#EDEDED" fontSize="9" textAnchor="middle" fontFamily="monospace">Embeddings</text>

                <path d="M 120 127 L 160 127" stroke="#555" strokeWidth="1" markerEnd="url(#arrow)" />

                <rect x="170" y="90" width="100" height="70" rx="8" fill="#1C1C1E" stroke="#4F46E5" strokeWidth="1.5" />
                <text x="220" y="120" fill="#EDEDED" fontSize="11" fontWeight="bold" textAnchor="middle">FAISS DB</text>
                <text x="220" y="140" fill="#8F8F93" fontSize="8" textAnchor="middle" fontFamily="monospace">Top-K Retrieval</text>

                <path d="M 270 125 L 305 125" stroke="#555" strokeWidth="1" markerEnd="url(#arrow)" />

                <rect x="310" y="105" width="80" height="40" rx="6" fill="#111" stroke="rgba(255,255,255,0.1)" />
                <text x="350" y="129" fill="#EDEDED" fontSize="9" textAnchor="middle" fontFamily="monospace">Reranker Node</text>

                <path d="M 350 145 L 350 210 L 270 210" stroke="#555" strokeWidth="1" markerEnd="url(#arrow)" />

                <rect x="160" y="190" width="100" height="40" rx="6" fill="#111" stroke="#10b981" strokeWidth="1" />
                <text x="210" y="214" fill="#10b981" fontSize="10" textAnchor="middle" fontFamily="monospace">Synthesized Prompt</text>
              </svg>
            </div>
          </div>
        )}

        {activeTab === 'mlops' && (
          <div className="capability-content fade-in">
            <div className="capability-info">
              <h3>Production AI Systems & MLOps</h3>
              <p>
                Taking models from scripts to high-availability endpoints. I specialize in backend scaling using FastAPI, containerizing setups with Docker, and setting up deployment workflows via GitHub Actions.
              </p>
              <ul className="cap-list">
                <li><strong>API Gateways:</strong> High-performance FastAPI routers handling concurrent async requests.</li>
                <li><strong>Container Isolation:</strong> Orchestrated Docker deployment pipelines managing services.</li>
                <li><strong>CI/CD Workflows:</strong> Automated test frameworks checking model inputs, schema linting, and deploy configurations.</li>
              </ul>
            </div>
            <div className="capability-visual">
              <svg viewBox="0 0 400 300" width="100%" height="100%" className="visual-svg">
                <defs>
                  <marker id="arrow" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                    <path d="M 0 0 L 10 5 L 0 10 z" fill="#555" />
                  </marker>
                </defs>
                <rect x="150" y="20" width="100" height="50" rx="6" fill="#111" stroke="rgba(255,255,255,0.1)" />
                <text x="200" y="49" fill="#EDEDED" fontSize="10" textAnchor="middle" fontFamily="monospace">GitHub Push</text>

                <path d="M 200 70 L 200 105" stroke="#555" strokeWidth="1" markerEnd="url(#arrow)" />

                <rect x="130" y="110" width="140" height="60" rx="8" fill="#1C1C1E" stroke="#4F46E5" strokeWidth="1.5" />
                <text x="200" y="135" fill="#EDEDED" fontSize="11" fontWeight="bold" textAnchor="middle">CI/CD Run</text>
                <text x="200" y="155" fill="#8F8F93" fontSize="8" textAnchor="middle" fontFamily="monospace">Lint & Validation Tests</text>

                <path d="M 200 170 L 200 205" stroke="#555" strokeWidth="1" markerEnd="url(#arrow)" />

                <rect x="140" y="210" width="120" height="55" rx="6" fill="#111" stroke="#10b981" strokeWidth="1" />
                <text x="200" y="234" fill="#10b981" fontSize="10" textAnchor="middle" fontFamily="monospace">Docker Deployment</text>
                <text x="200" y="250" fill="#8F8F93" fontSize="8" textAnchor="middle" fontFamily="monospace">AWS / Server Host</text>
              </svg>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
